// Shared wine-domain i18n keys (used by WineListing, WineFormModal, WineDetail).
// Page-specific keys live under `WineListing.*`. (All WineFormModal.* and WineDetail.*
// keys have been consolidated into shared `wine.*` keys.) The legacy `WineReceiving.*`
// keys remain in i18n.ts because they are reused by AddWineStock, WineReceivingV2,
// and 20260527_WineReceiving (out of scope here).

export const wineDict: Record<string, [en: string, th: string]> = {
  // ── Page titles / breadcrumbs ─────────────────────────────
  "wine.breadcrumb": ["Wines", "ไวน์"],

  // ── Shared field labels (used by listing table, form, detail) ──
  "wine.wine": ["Wine", "ไวน์"],
  "wine.name": ["Name", "ชื่อ"],
  "wine.category": ["Category", "หมวดหมู่"],
  "wine.subCategory": ["Sub Category", "หมวดหมู่ย่อย"],
  "wine.producer": ["Producer", "ผู้ผลิต"],
  "wine.grape": ["Grape", "องุ่น"],
  "wine.grapeDetail": ["Grape / Detail", "องุ่น / รายละเอียด"],
  "wine.vintage": ["Vintage", "ปี"],
  "wine.country": ["Country", "ประเทศ"],
  "wine.region": ["Region", "แหล่ง"],
  "wine.subRegion": ["Sub Region", "แหล่งย่อย"],
  "wine.size": ["Bottle Size", "ขนาดขวด"],
  "wine.serving": ["Serving", "การเสิร์ฟ"],
  "wine.sizeServing": ["Size / Serving", "ขนาด / การเสิร์ฟ"],
  "wine.minStock": ["Low-stock alert (bottles)", "เกณฑ์แจ้งเตือนใกล้หมด (ขวด)"],
  "wine.minStockHint": [
    "Alert when in-stock falls to this level or below. Empty = company default, 0 = off for this wine.",
    "แจ้งเตือนเมื่อคงเหลือ ≤ ค่านี้ (เว้นว่าง = ใช้ค่าบริษัท, 0 = ปิดเฉพาะรายการนี้)",
  ],
  "wine.alcohol": ["Alcohol", "แอลกอฮอล์"],
  "wine.marketPrice": ["Market Price", "ราคาตลาด"],
  "wine.purchasePrice": ["Purchase Price", "ราคาซื้อ"],
  "wine.sellingPrice": ["Selling Price", "ราคาขาย"],
  "wine.purchasePricePerBottle": ["Purchase Price (Per Bottle)", "ราคาซื้อ/ขวด"],
  "wine.inStock": ["In Stock", "คงเหลือ"],
  "wine.createdAt": ["Created At", "วันที่สร้าง"],
  "wine.dateAdded": ["Date Added", "วันที่เพิ่ม"],
  "wine.createdBy": ["Created By", "สร้างโดย"],
  "wine.wineId": ["Wine ID", "รหัสไวน์"],
  "wine.supplier": ["Supplier", "ซัพพลายเออร์"],
  "wine.purchaseDate": ["Purchase Date", "วันที่ซื้อ"],
  "wine.location": ["Location", "ตำแหน่ง"],
  "wine.receiptNo": ["Receipt No.", "เลขที่ใบรับ"],
  "wine.receiptItemLabel": ["receipt", "ใบรับ"],
  "wine.wineLabel": ["wine", "ไวน์"],
  "wine.bottle": ["bottle", "ขวด"],
  "wine.qty": ["Qty", "จำนวน"],
  "wine.date": ["Date", "วันที่"],
  "wine.status": ["Status", "สถานะ"],
  "wine.receiptRef": ["Receipt Ref", "เลขที่ใบรับ"],
  "wine.receiptRefHint": ["Auto-generated, or enter your invoice/PO reference", "สร้างอัตโนมัติ หรือใส่เลขที่ใบแจ้งหนี้/ใบสั่งซื้อ"],
  "wine.quantity": ["Quantity", "จำนวน"],
  "wine.note": ["Note", "หมายเหตุ"],

  // ── Filter dropdown placeholders (form field placeholders not translated) ──
  "wine.filter.category": ["Category", "หมวดหมู่"],
  "wine.filter.country": ["Country", "ประเทศ"],
  "wine.filter.region": ["Region", "แหล่ง"],
  "wine.filter.drinkingWindow": ["Drinking Window", "ช่วงเวลาดื่ม"],
  "wine.search": ["Search wines...", "ค้นหาไวน์..."],

  // ── Drinking window options ─────────────────────────────
  "wine.drinkingWindow.overdue": ["Overdue", "เลยช่วงแล้ว"],
  "wine.drinkingWindow.ready": ["Ready to drink", "พร้อมดื่ม"],
  "wine.drinkingWindow.wait": ["Wait to drink", "ยังไม่พร้อมดื่ม"],

  // ── Tabs (WineDetail) ───────────────────────────────────
  "wine.tab.details": ["Details", "รายละเอียด"],
  "wine.tab.inventory": ["Inventory", "คลัง"],
  "wine.tab.bottles": ["Bottle List", "รายการขวด"],
  "wine.tab.gallery": ["Gallery", "แกลเลอรี"],

  // ── WineDetail section / card titles ───────────────────
  "wine.wineSpecs": ["Wine Specifications", "รายละเอียดไวน์"],
  "wine.information": ["Information", "ข้อมูล"],

  // ── Tasting notes ───────────────────────────────────────
  "wine.tastingNotes": ["Tasting Notes", "บันทึกการชิม"],

  // ── Photos / images ─────────────────────────────────────
  "wine.photos": ["photos", "รูป"],

  // ── WineFormModal sections ──────────────────────────────
  "wine.form.tab.new": ["Add New Wine", "เพิ่มไวน์ใหม่"],
  "wine.form.tab.existing": ["Add One Stock (Add to Existing)", "เพิ่มสต๊อก 1 รายการ (ของเดิม)"],
  "wine.form.section.identity": ["Item details", "รายละเอียดรายการ"],
  "wine.form.section.pricing": ["Pricing, format & RFID", "ราคา ขนาดขวด และ RFID"],
  "wine.form.section.images": ["Images", "รูปภาพ"],
  "wine.form.section.details": ["{category} Details", "รายละเอียด {category}"],
  "wine.form.section.detailsHint": ["Optional fields specific to this category", "ฟิลด์เพิ่มเติมเฉพาะหมวดหมู่นี้"],
  "wine.form.stockIntake": ["Stock In (first receipt)", "รับเข้าสต๊อกครั้งแรก"],
  "wine.form.stockDetails": ["Receiving Details", "รายละเอียดการรับเข้า"],

  // ── Modal titles ───────────────────────────────────────
  "wine.modal.addStock": ["Add Stock", "เพิ่มสต็อก"],
  "wine.modal.edit": ["Edit {type}", "แก้ไข{type}"],
  "wine.modal.add": ["Add {type}", "เพิ่ม{type}"],
};
